import * as React from 'react';
import { injectIntl, intlShape } from 'react-intl';
import { object } from 'prop-types';
import { colors } from 'styles/provider';
import { VictoryAxis, VictoryChart, VictoryLabel, VictoryLine, VictoryScatter, VictoryTheme } from 'victory';
import moment from 'moment';
// import { Colors, Typography } from '../../../theme';
import {
  getCognitiveLoadData,
  getDiaryWeeklyMinAverageMax,
  getFatigueData,
  getMealData,
  getPhysicalLoadData,
  getRestData,
  getSleepData
} from './utils';
import { path } from 'ramda';

const Colors = {
  grayScale: {
    white: colors.clrWhite,
    lightestGray: colors.clrLightestGray,
    gray: colors.clrGray,
    darkGray: colors.clrDarkGray
  },
  warning: {
    default: colors.clrWarningDefault
  },
  brandBlue: {
    default: colors.clrBrandBlueDefault
  }
};

export const weekSummaryTableContent = (days, fmString) => [
  {
    values: ['min', 'k.a.', 'max']
  },
  {
    title: fmString('diary.fatigueDiary.fatigueTitle'),
    values: getDiaryWeeklyMinAverageMax(days, 'fatigueReports', 'fss'),
    color: 'rgba(240, 160, 31, 1)'
  },
  {
    title: fmString('diary.fatigueDiary.physicalStrainTitle'),
    values: getDiaryWeeklyMinAverageMax(days, 'fatigueReports', 'physicalStrain'),
    color: 'rgba(34, 34, 34, 1)'
  },
  {
    title: fmString('diary.fatigueDiary.cognitiveLoadTitle'),
    values: getDiaryWeeklyMinAverageMax(days, 'fatigueReports', 'cognitiveLoad'),
    color: 'rgba(194, 0, 18, 1)'
  },
  {
    title: fmString('diary.fatigueDiary.sleepTitle'),
    values: getDiaryWeeklyMinAverageMax(days, 'sleep', undefined, undefined, true),
    infoTextA: fmString('diary.fatigueDiary.sleepDuration'),
    infoTextB: fmString('diary.fatigueDiary.sleepQuality'),
    color: 'rgba(43, 56, 112, 1)'
  },
  {
    title: fmString('diary.fatigueDiary.mealsTitle'),
    values: getDiaryWeeklyMinAverageMax(days, 'meals'),
    color: 'rgba(4, 139, 130, 1)'
  },
  {
    title: fmString('diary.fatigueDiary.breaksTitle'),
    values: getDiaryWeeklyMinAverageMax(days, 'restBreaks'),
    color: 'rgba(43, 56, 112, 1)'
  }
];

const DiaryWeeklyRecapChart = ({ diary, intl: { formatMessage } }) => {
  const screenWidth = 1280;
  const fmString = id => formatMessage({ id });
  const { startDate, ...docData } = diary;

  const dayFatigues = Object.values(docData).map(d => {
    return getFatigueData(d);
  });

  const weekFatigues = dayFatigues.flatMap((d, i) => {
    if (!d) return { x: i * 24, y: null };
    return d.flatMap((f, j) => {
      f.x = f.x + i * 24;
      if (j === 0) {
        return [{ x: f.x, y: null }, { x: f.x, y: f.y }];
      }
      if (j === d.length - 1) {
        return [{ x: f.x, y: f.y }, { x: f.x, y: null }];
      }
      return { x: f.x, y: f.y };
    });
  });

  const dayPhysicals = Object.values(docData).map(d => {
    return getPhysicalLoadData(d);
  });

  const weekPhysicals = dayPhysicals.map((day, i) => {
    if (!day) return null;
    return day.map(p => {
      return p.map(s => {
        if (!s) return null;
        s.x = s.x + i * 24;
        return { x: s.x, y: -19, value: s.value };
      });
    });
  });

  const dayCognitive = Object.values(docData).map(d => {
    return getCognitiveLoadData(d);
  });

  const weekCognitive = dayCognitive.map((day, i) => {
    if (!day) return null;
    return day.map(c => {
      return c.map(s => {
        if (!s) return null;
        s.x = s.x + i * 24;
        return { x: s.x, y: -31, value: s.value };
      });
    });
  });

  const daySleeps = Object.values(docData).map(d => {
    return getSleepData(d);
  });

  const weekSleeps = daySleeps.map((day, i) => {
    if (!day) return null;
    return day.map(s => {
      if (!s) return null;
      s.x = s.x + i * 24;
      return { x: s.x, y: -43, quality: s.quality };
    });
  });

  const dayMeals = Object.values(docData).map(d => {
    return getMealData(d);
  });

  const weekMeals = dayMeals.map((day, i) => {
    if (!day) return null;
    return day.map(m => {
      if (!m) return null;
      m.x = m.x + i * 24;
      return { x: m.x, y: -63, symbol: 'square', size: 8 };
    });
  });

  const dayBreaks = Object.values(docData).map(d => {
    return getRestData(d);
  });

  const weekBreaks = dayBreaks.map((day, i) => {
    if (!day) return null;
    return day.map(b => {
      if (!b) return null;
      b.x = b.x + i * 24;
      return { x: b.x, y: -75, symbol: 'square', size: 8 };
    });
  });

  const dateFromPartial = [startDate[0], startDate[1] ? startDate[1] - 1 : 0, startDate[2] ? startDate[2] : 1];
  const startWeekday = new Date(moment(dateFromPartial)).getDay();

  return (
    <div>
      <VictoryChart
        domain={{ x: [0, 168], y: [-80, 100] }}
        width={screenWidth * 0.95}
        padding={{ bottom: 30 }}
        height={800}
        style={{
          background: {
            fill: Colors.grayScale.lightestGray
          }
        }}
        theme={VictoryTheme.material}
      >
        <VictoryAxis
          tickValues={[0, 24, 48, 72, 96, 120, 144, 168]}
          tickFormat={[
            fmString(`charts.weekDays.${startWeekday}`),
            fmString(`charts.weekDays.${startWeekday + 1 < 7 ? startWeekday + 1 : startWeekday + 1 - 7}`),
            fmString(`charts.weekDays.${startWeekday + 2 < 7 ? startWeekday + 2 : startWeekday + 2 - 7}`),
            fmString(`charts.weekDays.${startWeekday + 3 < 7 ? startWeekday + 3 : startWeekday + 3 - 7}`),
            fmString(`charts.weekDays.${startWeekday + 4 < 7 ? startWeekday + 4 : startWeekday + 4 - 7}`),
            fmString(`charts.weekDays.${startWeekday + 5 < 7 ? startWeekday + 5 : startWeekday + 5 - 7}`),
            fmString(`charts.weekDays.${startWeekday + 6 < 7 ? startWeekday + 6 : startWeekday + 6 - 7}`),
            ''
          ]}
          style={{
            grid: { stroke: Colors.grayScale.gray },
            tickLabels: {
              fontSize: 18
            }
          }}
          tickLabelComponent={<VictoryLabel dx={(screenWidth * 0.95) / 7 / 2} />}
        />
        <VictoryAxis dependentAxis tickValues={['']} />
        {weekFatigues && (
          <VictoryLine
            data={weekFatigues}
            style={{
              data: { stroke: Colors.warning.default }
            }}
          />
        )}
        {weekPhysicals &&
          weekPhysicals.map(day => {
            if (!day) return null;
            return day.map((d, j) => {
              return (
                <VictoryLine
                  key={`${j}physical`}
                  data={d}
                  style={{
                    data: {
                      strokeWidth: 15,
                      stroke: `rgba(34, 34, 34, ${
                        path([0, 'value'], d) === 0 ? 0.05 : (path([0, 'value'], d) || 0) * 0.1
                      })`
                    }
                  }}
                />
              );
            });
          })}
        {weekCognitive &&
          weekCognitive.map(day => {
            if (!day) return null;
            return day.map((d, j) => {
              return (
                <VictoryLine
                  key={`${j}physical`}
                  data={d}
                  style={{
                    data: {
                      strokeWidth: 15,
                      stroke: `rgba(194, 0, 18, ${
                        path([0, 'value'], d) === 0 ? 0.05 : (path([0, 'value'], d) || 0) * 0.1
                      })`
                    }
                  }}
                />
              );
            });
          })}
        {weekSleeps &&
          weekSleeps.map((d, i) => {
            if (!d) return null;
            return (
              <VictoryLine
                key={`${i}Sleep`}
                data={d}
                style={{
                  data: {
                    strokeWidth: 15,
                    stroke: `rgba(43, 56, 112, ${
                      path([0, 'quality'], d) === 0 ? 0.05 : (path([0, 'quality'], d) || 0) * 0.1
                    })`
                  }
                }}
              />
            );
          })}
        {weekMeals &&
          weekMeals.map((d, i) => {
            if (!d) return null;
            return (
              <VictoryScatter
                key={`${i}meal`}
                data={d}
                style={{
                  data: {
                    fill: 'rgba(4, 139, 130, 0.8)'
                  }
                }}
              />
            );
          })}
        {weekBreaks &&
          weekBreaks.map((d, i) => {
            if (!d) return null;
            return (
              <VictoryScatter
                key={`${i}break`}
                data={d}
                style={{
                  data: {
                    fill: '#2B3870'
                  }
                }}
              />
            );
          })}
      </VictoryChart>
    </div>
  );
};

const IntlWrappedDiary = injectIntl(DiaryWeeklyRecapChart);

DiaryWeeklyRecapChart.propTypes = {
  diary: object,
  intl: intlShape
};

export { IntlWrappedDiary as DiaryWeeklyRecapChart };
